import React, { memo, Fragment, useState, useContext } from 'react'
import { AuthContext } from '../context/auth-context'
import { useForm } from '../utils/form-hook'
import { useDotsAnimation } from '../utils/dot-animation-hook'
import { gql, useMutation } from '@apollo/client'
import { Row, Col, FormCheck, Form, Button, Alert, Spinner } from 'react-bootstrap'
import { Mail, Lock } from 'lucide-react'
import Card from '../components/common/Card'
import Logo from '../components/common/Logo'
import { Link, useNavigate } from 'react-router-dom'
import Notification from '../components/partials/Notification'
import { useOAuth2 } from '../utils/oauth2-hook'
import { OAUTH_LOGIN_CALLBACK_URL, OAUTH_LOGIN_PROVIDERS } from '../components/oauth/OAuthLoginCallback.jsx'
import LetterIcon from '@/components/icons/new/LetterIcon.jsx'
import PasswordInput from '@/components/common/PasswordInput.jsx'
import SignInWithSocial from '@/components/common/SignInWithSocial.jsx'

const LOGIN_USER = gql`
    mutation Login($loginInput: LoginInput) {
        login(loginInput: $loginInput) {
            token
        }
    }
`

const Login = memo(() => {
    const context = useContext(AuthContext)
    const { getAuth } = useOAuth2({
        redirectUri: OAUTH_LOGIN_CALLBACK_URL,
        responseType: 'code',
    })
    const navigate = useNavigate()
    const [errors, setError] = useState([])

    const loginUserCallback = (event) => {
        loginUser()
    }

    const { onChange, onSubmit, values, validated, validateEmail } = useForm(loginUserCallback, {
        email: '',
        password: '',
        rememberMe: false,
        context: 'admin',
    })

    const { animationRef } = useDotsAnimation()

    const [loginUser, { loading }] = useMutation(LOGIN_USER, {
        update(proxy, { data: { login: userData } }) {
            context.login(userData)
            navigate('/')
        },
        onError({ graphQLErrors }) {
            setError(graphQLErrors)
        },
        variables: { loginInput: { ...values, rememberMe: values.rememberMe === 'true' } },
    })

    const handleSsoLogin = (provider) => {
        getAuth({ ...provider, id: provider.name })
    }

    return (
        <Fragment>
            <section className="iq-auth-page" ref={animationRef}>
                <Row className="d-flex align-items-center justify-content-center vh-100 w-100">
                    <Col xs="10" sm="8" md="6" lg="4" xl="3" style={{ minWidth: '400px' }}>
                        <Card className="p-3">
                            <Card.Body>
                                <h4 className="text-center mb-4">Admin Sign In</h4>
                                <div className="d-flex justify-content-center mb-5">
                                    <Logo />
                                </div>
                                <Form noValidate validated={validated}>
                                    <Form.Group className="mb-4">
                                        <Form.Label>Email</Form.Label>
                                        <div className="form-control-icon-left">
                                            <span className="form-control-icon" aria-hidden="true">
                                                <LetterIcon size={20} />
                                            </span>
                                            <Form.Control
                                                required
                                                type="email"
                                                className="mb-0"
                                                placeholder="Enter email"
                                                name="email"
                                                onChange={onChange}
                                                onKeyUp={validateEmail}
                                            ></Form.Control>
                                        </div>
                                        <Form.Control.Feedback type="invalid">
                                            {values.email.length > 0 ? 'Please enter a valid Email' : ''}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-4">
                                        <Form.Label>Password</Form.Label>
                                        <PasswordInput
                                            required
                                            className="mb-0"
                                            placeholder="Enter password"
                                            name="password"
                                            onChange={onChange}
                                        />
                                    </Form.Group>
                                    <div className="d-flex justify-content-between mb-4">
                                        <Form.Check className="d-flex align-items-center gap-2 m-0">
                                            <FormCheck.Input
                                                onChange={(event) => {
                                                    event.target.value = event.target.checked
                                                    return onChange(event)
                                                }}
                                                type="checkbox"
                                                id="rememberMe"
                                                name="rememberMe"
                                            ></FormCheck.Input>
                                            <Form.Check.Label htmlFor="rememberMe" className="mb-0">
                                                Remember Me
                                            </Form.Check.Label>
                                        </Form.Check>
                                        <Link to="/reset-password" className="d-inline-block">
                                            Forgot your password?
                                        </Link>
                                    </div>
                                    <div className="pb-3">
                                        {errors.map((error, key) => {
                                            return (
                                                <Alert
                                                    key={key}
                                                    variant="warning rounded-0 alert-dismissible fade show"
                                                    role="alert"
                                                >
                                                    <span> {error.message} </span>
                                                </Alert>
                                            )
                                        })}
                                    </div>
                                    <div className="text-center mb-4 d-grid">
                                        <Button
                                            type="button"
                                            variant="btn btn-primary"
                                            onClick={onSubmit}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <Fragment>
                                                    <Spinner
                                                        as="span"
                                                        animation="grow"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />{' '}
                                                    Signing in...
                                                </Fragment>
                                            ) : (
                                                <Fragment>Sign in</Fragment>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                                <SignInWithSocial mode="login" onSocialLogin={handleSsoLogin} />

                                <p className="text-center">
                                    Don't have an account? <Link to="/signup">Click here to sign up.</Link>
                                </p>

                                <hr className="mb-4 w-100 border-0 border-top border-primary" />

                                <p className="text-center">
                                    <Link to="https://dashboard.turbamedia.io">Switch to Dashboard</Link>
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </section>
            <Notification />
        </Fragment>
    )
})

Login.displayName = 'Login'
export default Login
